/**
 * FunnelModal
 *
 * Full-screen modal overlay for PDP funnel experiences.
 * Converted from Emotion styled-components to Tailwind CSS.
 */

import { DialogPortal } from '../../components/shared/DialogPortal'
import type { FunnelOffer } from '../funnel-runtime.types'

interface FunnelModalProps {
  offer: FunnelOffer
  onAccept: () => void
  onDecline: () => void
  onDismiss: () => void
  accepting?: boolean
}

export function FunnelModal({ offer, onAccept, onDecline, onDismiss, accepting }: FunnelModalProps) {
  const savings = offer.originalPrice - offer.pricingValue

  return (
    <DialogPortal>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 animate-funnel-fade-in"
        onClick={onDismiss}
      >
        {/* Modal Card */}
        <div
          className="relative w-[90%] max-w-[480px] rounded-lg bg-tf-bg-card px-7 pb-7 pt-8 shadow-[0_8px_32px_rgba(0,0,0,0.2)] animate-funnel-slide-up"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            className="absolute right-3.5 top-3.5 border-none bg-transparent p-1 text-xl leading-none text-tf-text-sub cursor-pointer hover:text-tf-text"
            onClick={onDismiss}
            aria-label="Close"
          >
            &times;
          </button>

          {/* Offer Image */}
          {offer.imageUrl && (
            <img
              src={offer.imageUrl}
              alt={offer.headline}
              className="mb-4 h-[200px] w-full rounded-lg object-cover"
            />
          )}

          {/* Headline */}
          <h2 className="mb-2 text-[22px] font-bold text-tf-text">
            {offer.headline}
          </h2>

          {/* Description */}
          {offer.description && (
            <p className="mb-4 text-sm text-tf-text-sub">
              {offer.description}
            </p>
          )}

          {/* Price Row */}
          <div className="mb-5 flex items-baseline gap-3">
            <span className="text-[28px] font-extrabold text-tf-text">
              ${offer.pricingValue.toFixed(2)}
            </span>
            <span className="text-lg text-tf-text-sub line-through">
              ${offer.originalPrice.toFixed(2)}
            </span>
            {savings > 0 && (
              <span className="rounded-full bg-green-100 px-2.5 py-1 text-[13px] font-semibold text-green-700">
                Save ${savings.toFixed(2)}
              </span>
            )}
          </div>

          {/* CTA Button */}
          <button
            className="w-full rounded-lg bg-tf-text px-3.5 py-3.5 text-base font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
            onClick={onAccept}
            disabled={accepting}
          >
            {accepting ? 'Adding...' : offer.ctaText}
          </button>

          {/* Decline Link */}
          <button
            className="mx-auto mt-3 block border-none bg-transparent text-[13px] text-tf-text-sub underline cursor-pointer hover:text-tf-text"
            onClick={onDecline}
          >
            {offer.declineText}
          </button>
        </div>
      </div>
    </DialogPortal>
  )
}
